import {AxiosRequestConfig} from 'axios';

import Logger from '../../logger';
import {CandidateProfile, SavedCandidate} from '../../models';
import {getAuthToken} from '../../utils';
import {APIServiceImpl, ServiceResponse} from '../api';
import {
  CandidateSearchParams,
  ListSavedCandidatesParams,
  SaveCandidateParams,
  UpdateSavedCandidateParams,
} from './employer-candidate-params';
import {CandidateSearchResponse, EmployerCandidateService, SavedCandidateListResponse} from './employer-candidate.service';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

export class EmployerCandidateServiceImpl extends APIServiceImpl implements EmployerCandidateService {
  static readonly SEARCH_CANDIDATES = '/employer/candidates/search';
  static readonly SAVED_CANDIDATES = '/employer/candidates/saved';

  constructor() {
    super();
    this.service.interceptors.request.use(config => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async searchCandidates(params: CandidateSearchParams): Promise<ServiceResponse<CandidateSearchResponse>> {
    try {
      const config: AxiosRequestConfig = {
        params: this.serializeSearchParams(params),
        paramsSerializer: queryParams => this.serializeParams(queryParams),
      };

      const {data} = await this.get(EmployerCandidateServiceImpl.SEARCH_CANDIDATES, config);

      const page = data?.page ?? params.page ?? DEFAULT_PAGE;
      const limit = data?.limit ?? params.limit ?? DEFAULT_LIMIT;
      const total = data?.total ?? data?.data?.length ?? 0;
      const totalPages = data?.totalPages ?? data?.meta?.totalPages ?? 1;
      const hasMore = data?.hasMore ?? data?.meta?.hasMore ?? page < totalPages;
      const resultType = data?.resultType === 'EXACT_MATCH' || data?.resultType === 'SEMANTIC' ? data.resultType : 'SEMANTIC';

      const mapped: CandidateSearchResponse = {
        data: Array.isArray(data?.data) ? data.data.map((candidate: unknown) => new CandidateProfile(candidate)) : [],
        hasMore,
        limit,
        page,
        resultType,
        total,
        totalPages,
        searchSummary: typeof data?.searchSummary === 'string' ? data.searchSummary : undefined,
      };

      return new ServiceResponse(mapped);
    } catch (error: unknown) {
      const message =
        (error as {response?: {data?: {message?: string}; status?: number}}).response?.data?.message ||
        (error as Error).message ||
        String((error as {response?: {status?: number}}).response?.status ?? '');
      Logger.error(`GET ${EmployerCandidateServiceImpl.SEARCH_CANDIDATES}: ${message}`);
      return new ServiceResponse<CandidateSearchResponse>(undefined, message);
    }
  }

  async listSavedCandidates(params?: ListSavedCandidatesParams): Promise<ServiceResponse<SavedCandidateListResponse>> {
    try {
      const config: AxiosRequestConfig = {
        params,
        paramsSerializer: queryParams => this.serializeParams(queryParams),
      };
      const {data} = await this.get(EmployerCandidateServiceImpl.SAVED_CANDIDATES, config);
      const mapped: SavedCandidateListResponse = {
        data: Array.isArray(data?.data) ? data.data.map((entry: unknown) => new SavedCandidate(entry)) : [],
        limit: data?.limit ?? params?.limit ?? DEFAULT_LIMIT,
        page: data?.page ?? params?.page ?? DEFAULT_PAGE,
        total: data?.total ?? data?.data?.length ?? 0,
        totalPages: data?.totalPages ?? data?.meta?.totalPages ?? 1,
      };
      return new ServiceResponse(mapped);
    } catch (error: unknown) {
      const message =
        (error as {response?: {data?: {message?: string}; status?: number}}).response?.data?.message ||
        (error as Error).message ||
        String((error as {response?: {status?: number}}).response?.status ?? '');
      Logger.error(`GET ${EmployerCandidateServiceImpl.SAVED_CANDIDATES}: ${message}`);
      return new ServiceResponse<SavedCandidateListResponse>(undefined, message);
    }
  }

  async saveCandidate(params: SaveCandidateParams): Promise<ServiceResponse<SavedCandidate>> {
    try {
      const {data} = await this.post(EmployerCandidateServiceImpl.SAVED_CANDIDATES, params);
      return new ServiceResponse(new SavedCandidate(data));
    } catch (error: unknown) {
      const message =
        (error as {response?: {data?: {message?: string}; status?: number}}).response?.data?.message ||
        (error as Error).message ||
        String((error as {response?: {status?: number}}).response?.status ?? '');
      Logger.error(`POST ${EmployerCandidateServiceImpl.SAVED_CANDIDATES}: ${message}`);
      return new ServiceResponse<SavedCandidate>(undefined, message);
    }
  }

  async updateSavedCandidate(params: UpdateSavedCandidateParams): Promise<ServiceResponse<SavedCandidate>> {
    try {
      const url = `${EmployerCandidateServiceImpl.SAVED_CANDIDATES}/${params.savedCandidateId}`;
      const payload = {
        note: params.note,
        tags: params.tags,
      };
      const {data} = await this.patch(url, payload);
      return new ServiceResponse(new SavedCandidate(data));
    } catch (error: unknown) {
      const message =
        (error as {response?: {data?: {message?: string}; status?: number}}).response?.data?.message ||
        (error as Error).message ||
        String((error as {response?: {status?: number}}).response?.status ?? '');
      Logger.error(`PATCH ${EmployerCandidateServiceImpl.SAVED_CANDIDATES}: ${message}`);
      return new ServiceResponse<SavedCandidate>(undefined, message);
    }
  }

  async removeSavedCandidate(savedCandidateId: string): Promise<ServiceResponse<void>> {
    try {
      const url = `${EmployerCandidateServiceImpl.SAVED_CANDIDATES}/${savedCandidateId}`;
      await this.delete(url);
      return new ServiceResponse<void>(undefined);
    } catch (error: unknown) {
      const message =
        (error as {response?: {data?: {message?: string}; status?: number}}).response?.data?.message ||
        (error as Error).message ||
        String((error as {response?: {status?: number}}).response?.status ?? '');
      Logger.error(`DELETE ${EmployerCandidateServiceImpl.SAVED_CANDIDATES}: ${message}`);
      return new ServiceResponse<void>(undefined, message);
    }
  }

  private serializeSearchParams(params: CandidateSearchParams) {
    return {
      auraIds: params.auraIds,
      deep: params.deep ? 'true' : undefined,
      experienceMax: params.experienceMax,
      experienceMin: params.experienceMin,
      industries: params.industries,
      limit: params.limit,
      location: params.location,
      page: params.page,
      q: params.query,
      resultType: params.resultType,
      skills: params.skills,
    };
  }

  private serializeParams(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach(entry => {
          if (entry !== undefined && entry !== null) {
            searchParams.append(key, String(entry));
          }
        });
        return;
      }
      searchParams.append(key, String(value));
    });
    return searchParams.toString();
  }
}
